import { addPixelToStyle } from './utils/addPixelToStyle';
import { getApiEndpoint } from './utils/getApiEndpoint';
import { translate } from './utils/translate';
import { getSpeedometerHtml, speedometer } from './speedometer';

const VOTE_TYPE = {
  PERCENTAGE: 'percentage',
  ABSOLUTE_COUNT: 'absoluteCount'
};
const NPS_SURVEY_TYPE = 'nps';
const DEFAULT_SURVEY_TYPE = 'default';
const SPEEDOMETER_SURVEY_TYPE = 'speedometer';
const TEXT_MAX_LENGTH = 1000;
const FREE_TEXT_VOTES_INDEX = 'freeTextVotes';
const FREE_TEXT_VOTES_ID = 'text';

class CleverPushSurvey {
  constructor() {
    if (window.cleverPushSurveyInitDone) {
      console.warn('CleverPush surveys already initialized. Please include the cleverpush-survey script only once.');
      return;
    }
    this.completedSurveys = [];
    this.currentSurveyAnswers = [];
    this.surveys = {};
    this.answerSelected = '';

    this.apiEndpoint = getApiEndpoint();
    this.initSurveys();
    this.attributes = {};

    window.cleverPushSurveyInitDone = true;
  }

  votingAllowed(survey) {
    const surveyAlreadyVotedKey = `cleverpush-${survey._id}-survey-already-voted`;
    let surveryAlreadyVoted = false;
    try {
      surveryAlreadyVoted = localStorage.getItem(surveyAlreadyVotedKey);
    } catch (error) {
      console.log(error);
    }
    return !surveryAlreadyVoted || survey.allowMultipleVotesPerUser;
  }

  initSurveys() {
    const domForms = document.querySelectorAll('.cleverpush-survey');
    for (let i = 0; i < domForms.length; i += 1) {
      if (domForms[i].dataset.apiEndpoint) {
        this.apiEndpoint = domForms[i].dataset.apiEndpoint;
      }

      this.getSurvey(domForms[i].dataset.id, this.completedSurveys, (config) => {
        if (domForms[i].dataset.embed === 'true') {
          config.embed = true;
        }
        this.initSurvey(domForms[i], config);
        this.initSpeedometer(config);
      });
    }
  }

  /**
   * @function
   * @param {String} id Survey id
   * @param {() => {}} callback callback function
   */
  getSurvey(id, completedSurveys, callback) {
    let params = '';
    completedSurveys.forEach((surveyId) => {
      params += `completedSurveys[]=${surveyId}&`;
    });
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${this.apiEndpoint}/survey/${id}/config?${params}`, true);
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4 && xhr.status === 200) {
        const json = JSON.parse(xhr.responseText);
        callback(json);
      }
    };
    xhr.send();
  }

  invertHex(hex) {
    if (hex.indexOf('#') === 0) {
      hex = hex.slice(1);
    }
    // convert 3-digit hex to 6-digits.
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length !== 6) {
      throw new Error('Invalid HEX color.');
    }
    // invert color components
    const r = (255 - parseInt(hex.slice(0, 2), 16)).toString(16);
    const g = (255 - parseInt(hex.slice(2, 4), 16)).toString(16);
    const b = (255 - parseInt(hex.slice(4, 6), 16)).toString(16);
    // pad each with zeros and return
    return `#${this.padZero(r)}${this.padZero(g)}${this.padZero(b)}`;
  }

  padZero(str, len) {
    len = len || 2;
    const zeros = new Array(len).join('0');
    return (zeros + str).slice(-len);
  }

  getCurrentQuestion = (surveyId) => {
    const questionsBySurvey = this.surveys[surveyId].questions;
    const currentQuestionIndex = this.surveys[surveyId].currentQuestionIndex || 0;
    return questionsBySurvey[currentQuestionIndex] || {};
  }

  initClickListeners(surveyDiv, config, survey) {
    const answers = surveyDiv.querySelectorAll('.cleverpush-survey-answer, .cleverpush-survey-answer-checkbox');
    for (let i = 0; i < answers.length; i += 1) {
      answers[i].addEventListener('click', (event) => {
        const answerSurveyType = answers[i].dataset.surveyType;
        this.handleOnAnswerClick({
          event, answerDivThis: answers[i], isCommentClicked: false, config, survey, answerSurveyType
        });
      });
    }
    const nextButtons = surveyDiv.querySelectorAll('.cleverpush-survey-button-next, .cleverpush-survey-button-submit');
    nextButtons.forEach((nextButton) => {
      if (nextButton.dataset.clickListener === 'false') {
        return;
      }
      nextButton.addEventListener('click', (event) => {
        const surveyId = nextButton.dataset?.id;
        if (nextButton.className.includes('cleverpush-survey-button-single')) {
          const currentQuestion = this.getCurrentQuestion(surveyId);
          this.handleOnAnswerClick({
            event, answerDivThis: nextButton, isCommentClicked: true, config, survey, answerSurveyType: currentQuestion.type
          });
        } else {
          this.handleOnNextButtonClick({
            event, nextButton, config, survey
          });
        }
      });
    });
    const otherAnswer = surveyDiv.querySelector('.cleverpush-survey-other-answer');
    const bottom = surveyDiv.querySelector('.cleverpush-survey-buttons-bottom');
    if (otherAnswer) {
      if (bottom.style.display === '') {
        bottom.style.display = 'flex';
      }
      otherAnswer.addEventListener('click', () => {
        const textInput = surveyDiv.querySelector('.cleverpush-survey-text-answer');
        const showBelow = this.getCurrentQuestion(otherAnswer.dataset?.id).textAnswerButtonBelow;
        if (textInput.style.display === 'none') {
          textInput.style.display = 'flex';
        } else {
          textInput.style.display = 'none';
        }
        if (bottom) {
          if (!showBelow) {
            bottom.style.display = bottom.style.display === 'flex' ? 'none' : 'flex';
          } else if (this.getCurrentQuestion(otherAnswer.dataset?.id).answerChoice !== 'multiple') {
            bottom.style.display = textInput.style.display;
          }
        }
      });
    }
  }

  shouldHandleMatchRule = (matchRule) => matchRule && matchRule.type === 'question';

  handleMatchRule = ({
    matchRule, div, questionsBySurvey, survey, config, surveyId,
  }) => {
    const currentQuestionIndex = matchRule.nextQuestionIndex;

    div.innerHTML = this.getSurveyHtml({
      question: questionsBySurvey[currentQuestionIndex],
      questionIndex: currentQuestionIndex,
      survey,
    });

    this.initClickListeners(div, config, survey);
    this.surveys[surveyId].currentQuestionIndex = currentQuestionIndex;
  };

  handleOnNextButtonClick = ({
    event, nextButton, config, survey
  }) => {
    if (event) {
      event.preventDefault();
    }
    const surveyId = nextButton.dataset?.id;
    const div = document.querySelector(`.cleverpush-survey-${surveyId}`);
    const answers = div.querySelectorAll('.cleverpush-survey-answer');
    const answerIndexes = [];
    const questionsBySurvey = this.surveys[surveyId].questions;
    let currentQuestionIndex = this.surveys[surveyId].currentQuestionIndex || 0;
    const currentQuestion = this.getCurrentQuestion(surveyId);
    const isLastQuestion = currentQuestionIndex === questionsBySurvey.length - 1;
    const text = document.getElementById(`cleverpush-survey-text-${currentQuestionIndex}`);

    if (text && text.value.trim() !== '' && text.style.display !== 'none') {
      this.handleOnAnswerClick({
        event: null,
        answerDivThis: text,
        isCommentClicked: true,
        config,
        survey,
        answerSurveyType: currentQuestion.type
      });
    }

    answers.forEach((ans, ansIndex) => {
      if (ans.children[0].children[0].checked) {
        answerIndexes.push(ansIndex);
      }
    });

    if ((currentQuestion.answerChoice === 'multiple' && answerIndexes.length === 0) || (text && document.querySelector('.cleverpush-survey-text-answer').style.display !== 'none' && text.value.trim() === '' && !currentQuestion.textAnswersOptional)) {
      alert(translate('alert.enterTextAnswer'));
      return;
    }

    answerIndexes.forEach((answeredIndex) => {
      const currentAnswer = questionsBySurvey[currentQuestionIndex].answers[answeredIndex];
      this.saveSurveyAnswer({
        questionId: currentQuestion.id,
        answerId: currentAnswer?.id,
        text: currentAnswer?.title,
        surveyId,
      });
    });

    this.updateVoteCounts({
      questionIndex: currentQuestionIndex, answerIndexes, currentQuestion, surveyId, isLastQuestion
    });

    const currentSurveyDiv = document.querySelector(`.cleverpush-survey-${surveyId}`);

    const matchRule = survey.rules.find((rule) => this.getMatchedRule(rule, currentQuestionIndex, answerIndexes));
    const shouldJumpToResult = matchRule && matchRule.jumpToResults && matchRule.type === 'result';

    if (shouldJumpToResult) {
      this.handleSubmit({ config, surveyDiv: currentSurveyDiv, survey });
      return;
    }

    if (this.shouldHandleMatchRule(matchRule)) {
      this.handleMatchRule({
        matchRule,
        div,
        questionsBySurvey,
        survey,
        config,
        surveyId,
      });
    } else if (currentQuestionIndex < questionsBySurvey.length - 1) {
      currentQuestionIndex += 1;
      div.innerHTML = this.getSurveyHtml({
        question: questionsBySurvey[currentQuestionIndex],
        questionIndex: currentQuestionIndex,
        survey,
      });
      this.initClickListeners(div, config, survey);
      this.surveys[surveyId].currentQuestionIndex = currentQuestionIndex;
    }

    if ((this.surveys[surveyId].results && isLastQuestion)) {
      this.handleSubmit({ config, surveyDiv: currentSurveyDiv, survey });
    }
  };

  saveSurveyAnswer = ({
    questionId, answerId, text, surveyId
  }) => {
    // const existingAnswerIndex = this.currentSurveyAnswers.findIndex((answer) => answer.questionId === questionId);
    // if (existingAnswerIndex > -1) {
    //   this.currentSurveyAnswers.splice(existingAnswerIndex, 1);
    // }
    if (!questionId || !answerId) {
      return;
    }
    this.currentSurveyAnswers.push({
      questionId,
      answerId,
      text,
    });

    try {
      localStorage.setItem(`currentSurveyAnswers.${surveyId}`, JSON.stringify(this.currentSurveyAnswers || []));
    } catch (error) {
      console.warn('[CleverPush Survey] Error saving survey answers to localStorage');
    }
  }

  handleOnAnswerClick = ({
    event, answerDivThis, isCommentClicked, config, survey, answerSurveyType = 'default'
  }) => {
    if (event) {
      event.preventDefault();
    }

    const questionIndex = parseInt(answerDivThis.dataset?.question, 10);
    const answerIndex = parseInt(answerDivThis.dataset?.answer, 10);
    const isSingleButton = answerDivThis.className.includes('cleverpush-survey-button-single');
    const surveyId = answerDivThis.dataset?.id;
    if (!this.surveys[surveyId]) {
      return;
    }

    const questionsBySurvey = this.surveys[surveyId]?.questions;
    const currentQuestion = questionsBySurvey[questionIndex];
    const isLastQuestion = questionIndex === questionsBySurvey.length - 1;
    let currentQuestionIndex = this.surveys[surveyId].currentQuestionIndex || 0;

    if (currentQuestion.answered && answerSurveyType !== NPS_SURVEY_TYPE) {
      return;
    }
    this.answerSelected = answerDivThis.innerText;

    if (isCommentClicked) {
      const textValue = document.getElementById(`cleverpush-survey-text-${questionIndex}`).value.trim();
      if (!textValue && !questionsBySurvey[questionIndex].textAnswersOptional) {
        alert(translate('alert.enterTextAnswer'));
        return;
      }

      this.saveSurveyAnswer({
        questionId: currentQuestion.id,
        answerId: FREE_TEXT_VOTES_ID,
        text: textValue,
        surveyId,
      });

      this.sendVotes({
        surveyId,
        questionIndex,
        answerIndex: isNaN(answerIndex) ? FREE_TEXT_VOTES_INDEX : answerIndex,
        isLastQuestion,
      });
    } else {
      if (currentQuestion.answerChoice === 'multiple') {
        const answerCheck = document.getElementById(`checkbox-${answerIndex}`);
        answerCheck.checked = !answerCheck.checked;
        return;
      }

      const currentAnswer = questionsBySurvey[currentQuestionIndex].answers[answerIndex];
      this.saveSurveyAnswer({
        questionId: currentQuestion?.id,
        answerId: currentAnswer?.id,
        text: currentAnswer?.title,
        surveyId,
      });

      this.updateVoteCounts({
        questionIndex, answerIndexes: [answerIndex], currentQuestion, surveyId, isLastQuestion
      });
    }

    const div = document.querySelector(`.cleverpush-survey-${surveyId}`);
    const matchRule = survey.rules.find((rule) => this.getMatchedRule(rule, questionIndex, [answerIndex]));
    const shouldJumpToResult = matchRule && matchRule.jumpToResults && matchRule.type === 'result';

    if (shouldJumpToResult) {
      this.handleSubmit({ config, surveyDiv: div, survey });
      return;
    }

    if (this.shouldHandleMatchRule(matchRule)) {
      this.handleMatchRule({
        matchRule,
        div,
        questionsBySurvey,
        survey,
        config,
        surveyId,
      });
    } else if (currentQuestionIndex < questionsBySurvey.length - 1) {
      currentQuestionIndex += 1;

      div.innerHTML = this.getSurveyHtml({
        question: questionsBySurvey[currentQuestionIndex],
        questionIndex: currentQuestionIndex,
        survey,
      });
      this.initClickListeners(div, config, survey);
      this.surveys[surveyId].currentQuestionIndex = currentQuestionIndex;
    }

    if ((this.surveys[surveyId].results && isLastQuestion)) {
      this.handleSubmit({ config, surveyDiv: div, survey });
    }
    this.initSpeedometer(config);
  };

  /**
   * This function will initialize the survey
   *
   * @function
   * @param {HTMLElement} surveyDiv Survey div
   * @param {object} config config object
   */
  initSurvey(surveyDiv, config) {
    if (!surveyDiv) {
      return;
    }

    this.currentSurveyAnswers = [];

    const survey = config.survey;

    if (survey._id) {
      try {
        this.currentSurveyAnswers = JSON.parse(localStorage.getItem(`currentSurveyAnswers.${survey._id}`)) || [];
      } catch (error) {
        console.warn('[CleverPush Survey] Error parsing survey data from localStorage');
      }
    }

    const classNameId = `cleverpush-survey-${survey._id}`;
    surveyDiv.className += ` ${classNameId}`;
    const prefix = `.${classNameId}`;

    const style = document.createElement('style');
    style.innerHTML = `
    ${prefix} { ${addPixelToStyle(config.style || {})} ${addPixelToStyle(survey.surveyStyle || {})} }
    ${prefix} .cleverpush-survey-question { ${addPixelToStyle(config.questionStyle || {})} ${addPixelToStyle(survey.surveyQuestionStyle || {})} }
    ${prefix} .cleverpush-survey-question-title { margin-bottom: 15px; font-weight: bold; }
    ${prefix} .cleverpush-survey-answer { display: flex; flex-wrap: wrap; align-items: center; margin-bottom: 10px; ${addPixelToStyle(config.answerStyle || {})} ${addPixelToStyle(survey.surveyAnswerStyle || {})} }
    ${prefix} .cleverpush-survey-answer:hover { opacity: 0.8; }
    ${prefix} .cleverpush-survey-answer:active { opacity: 0.9; }
    ${prefix} .cleverpush-survey-answer-title { font-weight: 500; display: flex; align-items: center; }
    @media (max-width: 767px) { ${prefix} .cleverpush-survey-answer-title { width: 100%; margin-bottom: 5px; } }
    ${prefix} .cleverpush-survey-answer-bar { overflow: hidden; border-radius: 3px; min-width: 150px; margin-left: 20px; }
    ${prefix} .cleverpush-survey-answer-bar-inner { transition: 0.2s all ease-in-out; font-weight: normal; background-color: ${(survey.surveyAnswerStyle?.color || config.answerStyle?.color || '#fff')};  height: 16px; font-size: 13px; color: ${(survey.surveyAnswerStyle?.color || config.answerStyle?.color || '#fff')}; text-align: left; }
    ${prefix} .cleverpush-survey-answer-text { font-weight: normal; margin-left: auto; }
    ${prefix} .cleverpush-survey-answer-voted { cursor: default; justify-content: space-between; }
    @media (max-width: 767px) { ${prefix} .cleverpush-survey-answer-voted { width: 100% !important;} }
    ${prefix} .cleverpush-survey-result { display: flex; align-items: center; }
    ${prefix} .cleverpush-survey-answer-checkbox { height: 18px; width: 18px; margin-right: 10px; pointer-events: none; }
    ${prefix} .cleverpush-survey-buttons { display: flex; justify-content: center; }
    ${prefix} .cleverpush-survey-buttons .cleverpush-survey-button { padding: 12px 25px; font-size: 18px; font-weight: bold; cursor: pointer; color:  ${(survey.surveyAnswerStyle?.color || config.answerStyle?.color || '#fff')}; background-color: ${(survey.surveyAnswerStyle?.backgroundColor || config.answerStyle?.backgroundColor || '#1338a0')}; border: none; border-radius: 3px; font-family: ${(survey.surveyAnswerStyle?.fontFamily || config.answerStyle?.fontFamily || 'sans-serif')}; ${addPixelToStyle(config.buttonStyles || {})} }
    ${prefix} .cleverpush-survey-buttons .cleverpush-survey-button:hover { opacity: 0.8; }
    ${prefix} .cleverpush-next-survey { padding: 12px 25px; font-size: 18px; font-weight: bold; cursor: pointer; color:  ${(survey.surveyAnswerStyle?.color || config.answerStyle?.color || '#fff')}; background-color: ${(survey.surveyAnswerStyle?.backgroundColor || config.answerStyle?.backgroundColor || '#1338a0')}; border: none; border-radius: 3px; font-family: ${(survey.surveyAnswerStyle?.fontFamily || config.answerStyle?.fontFamily || 'sans-serif')}; ${addPixelToStyle(config.buttonStyles || {})} }
    ${prefix} .cleverpush-survey-text-answer { font-size: 16px; border-radius: 6px; font-weight: bold; cursor: pointer; display:block; overflow:hidden; position: relative; color: ${(survey.surveyAnswerStyle?.color || config.answerStyle?.color || '#fff')}; background-color: ${(survey.surveyAnswerStyle?.color || config.answerStyle?.color || '#1338a0')}; }
    ${prefix} .cleverpush-survey-text-inputanswer { width: 100%; border-radius: 4px; border: solid; font-size: 16px;} 
    ${prefix} .cleverpush-survey-text-inputanswer:focus { outline: none }
    ${prefix} .cleverpush-survey-answers-label { display: flex; justify-content: space-between; margin-bottom: 10px; }
    ${prefix} .cleverpush-survey-answer-label { font-size: ${(survey.surveyLabelStyle?.fontSize || config.labelStyle?.fontSize || '12px')}; color:${(survey.surveyLabelStyle?.color || config.labelStyle?.color || '#AEAEAE')}; font-weight: ${(survey.surveyLabelStyle?.fontWeight || config.labelStyle?.fontWeight || '')}; font-style: ${(survey.surveyLabelStyle?.fontStyle || config.labelStyle?.fontStyle || '')}}
    ${prefix} .cleverpush-survey-speedometer-container {position: relative;  margin: auto; max-width: 500px; background-color: white; }
    ${prefix} .cleverpush-survey-header-text {display: flex; justify-content: center; flex-direction: column; align-items: center; font-family: Georgia, serif; text-align: center;}
    ${prefix} .cleverpush-survey-speedometer-question {font-size: 20px;line-height: 5vh;font-weight:bold ;}
    ${prefix} .cleverpush-survey-bottom {display: flex; justify-content: space-between; margin-left: 16px; margin-right: 12px; flex-direction: row; margin-top: -40px; user-select: none;}
    ${prefix} .cleverpush-survey-bottom p {max-width: 40%; color: #333; font-weight: 400; font-size: 20px;font-family: Arial, sans-serif;}
    ${prefix} .cleverpush-percentages { user-select: none;}
    ${prefix} #cleverpush-survey-votehandle {cursor: pointer;}
    ${prefix} .cleverpush-pulse { animation: cleverpush-pulse-animation 1.3s ease-in-out infinite; touch-action: none;}
    ${prefix} .cleverpush-footer-outer { display: flex; flex-direction: column; align-items: end; user-select: none;}
    ${prefix} .cleverpush-live-info { display: flex; align-items: center; justify-content: center; padding-left: 20px; color: rgb(51, 51, 51); font-family: Arial, sans-serif; text-transform: uppercase;max-width: 100%; margin-bottom:'30px'}
    ${prefix} .cleverpush-pulse-beacon { overflow: visible; stroke-width: 2px;stroke-opacity: 1; fill: none; transform-origin: center; animation: cleverpush-beacon-animation 2s linear infinite;}
    ${prefix} .cleverpush-bold {text-anchor: middle; font-weight: 700; alignment-baseline: middle; fill: rgb(0, 0, 0); font-size: 20px;}
    ${prefix} .cleverpush-survey-input-container {display: inline-flex; gap: 8px; align-items: center; width: 100%;}
    @media (max-width: 767px) { ${prefix} .cleverpush-survey-input-container {flex-direction: column;}}
    @keyframes cleverpush-beacon-animation {0% {stroke-width: 5px;stroke-opacity: 1;transform: scale(0.3);} 100% { stroke-width: 0; stroke-opacity: 0; transform: scale(2);}}
    @keyframes cleverpush-pulse-animation {
        0% { -webkit-transform: scale(1); transform: scale(1); }
        50% { -webkit-transform: scale(1.1); transform: scale(1.1);}
        100% { -webkit-transform: scale(1); transform: scale(1);} 
      }
    `;
    document.head.appendChild(style);

    if (config.customCss) {
      const node = document.createElement('style');
      node.innerHTML = config.customCss;
      document.body.appendChild(node);
    }

    if (survey.customCss) {
      const node = document.createElement('style');
      node.innerHTML = survey.customCss;
      document.head.appendChild(node);
    }

    if (new Date(survey.expirationDate) && new Date(survey.expirationDate) < new Date()) {
      this.displayResults({
        surveyDiv, config, survey,
      });

      if (config.embed && window.parent) {
        window.parent.postMessage({
          sentinel: 'amp',
          type: 'embed-size',
          height: document.body.scrollHeight
        }, '*');
      }
      return;
    }

    const votingAllowed = this.votingAllowed(survey);

    let currentQuestionIndex = 0;
    if (!votingAllowed) {
      this.displayResults({
        surveyDiv, config, survey,
      });
      return;
    }

    let questionAlreadyAnswered = null;
    try {
      questionAlreadyAnswered = JSON.parse(localStorage.getItem(`cleverpush-${survey._id}-survey-question-answered`));
    } catch (error) {
      console.warn('[CleverPush Survey]', error);
    }
    if (questionAlreadyAnswered && survey.questions[questionAlreadyAnswered.questionIndex + 1]) {
      currentQuestionIndex = questionAlreadyAnswered.questionIndex + 1;
    }

    const questions = (survey.questions || []).map((question) => ({ ...question, answered: false }));
    survey.questions = questions;
    this.surveys = { ...this.surveys, [survey._id]: { ...survey, currentQuestionIndex } };
    surveyDiv.innerHTML = this.getSurveyHtml({
      question: this.surveys[survey._id].questions[this.surveys[survey._id].currentQuestionIndex],
      questionIndex: this.surveys[survey._id].currentQuestionIndex,
      survey,
    });
    this.initClickListeners(surveyDiv, config, survey);
    this.setAttributes(config);
    if (config.embed && window.parent) {
      window.parent.postMessage({
        sentinel: 'amp',
        type: 'embed-size',
        height: document.body.scrollHeight
      }, '*');
    }
    document.querySelectorAll('.cleverpush-survey textarea').forEach((textarea) => {
      textarea.addEventListener('focus', () => {
        setTimeout(() => {
          textarea.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 300);
      });
    });
  }

  /**
   * @function
   * @param {object} config configuration object
   * @param {HTMLElement} surveyDiv survey div
   */
  handleSubmit({ config, surveyDiv, survey }) {
    try {
      this.displayResults({ surveyDiv, config, survey });
      if (this.matchRule) {
        const result = (survey.results || []).find((r, resultIndex) => resultIndex === this.matchRule.resultIndex);
        this.displayAppropriateResult(surveyDiv, result);
      } else if (survey.results && survey.results.length > 0) {
        const result = survey.results[0];
        this.displayAppropriateResult(surveyDiv, result);
      }
    } catch (error) {
      this.displayResults({ surveyDiv, config, survey });
    }
  }

  /**
   * This function will update the current question's vote counts
   *
   * @function
   * @param {object} config config object
   * @param {Number} questionIndex question index
   * @param {Number} answerIndexes answer indexes
   * @param {object} currentQuestion current question
   */
  updateVoteCounts({
    questionIndex, answerIndexes, currentQuestion, surveyId, isLastQuestion
  }) {
    const rules = this.surveys[surveyId].rules || [];
    const results = this.surveys[surveyId].results || [];
    if ((rules && rules.length > 0) && (results && results.length > 0)) {
      const resultRules = rules.filter((rule) => rule.type === 'result' || rule.resultIndex !== null);
      const matchRule = resultRules.find((rule) => this.getMatchedRule(rule, questionIndex, answerIndexes));
      if (matchRule) {
        this.matchRule = matchRule;
      }
    }

    const answerTexts = [];
    answerIndexes.forEach((answerIndex) => {
      if (!this.surveys[surveyId].questions[questionIndex].answers[answerIndex].voted) {
        this.surveys[surveyId].questions[questionIndex].answers[answerIndex].voted = 0;
      }
      this.surveys[surveyId].questions[questionIndex].answers[answerIndex].voted = parseInt(this.surveys[surveyId].questions[questionIndex].answers[answerIndex].voted, 10) + 1;
      this.sendVotes({
        surveyId,
        questionIndex,
        answerIndex,
        isLastQuestion,
      });

      answerTexts.push(this.surveys[surveyId].questions[questionIndex].answers[answerIndex].title);
    });

    currentQuestion.answered = true;

    if (typeof dataLayer !== 'undefined' && dataLayer.push) {
      try {
        dataLayer.push({
          event: 'virtualPageviewSurvey',
          virtualPageURL: `${window.location.pathname}#cleverPushSurvey-${surveyId}`,
        });
      } catch (error) {
        console.info(error);
      }
    }

    try {
      if (typeof window.top._satellite !== 'undefined' && window.top._satellite.track) {
      // For customer: KSTA
        window.top._satellite.track('MicrosurveyAnswer', {
          surveyid: surveyId,
          stepnumber: questionIndex + 1,
          answer: answerTexts.join(', '),
        });
      }
    } catch (error) {
      console.info(error);
    }
  }

  /**
   * This function will give match rule
   *
   * @function
   * @param {object} rule rule of survey
   * @param {Number} questionIndex question index
   * @param {Number} selectedAnwerIndexes  User Selected Anwer Indexes
   *
   */

  getMatchedRule(rule, questionIndex, selectedAnwerIndexes) {
    if (isNaN(rule.questionIndex) || rule.questionIndex !== questionIndex) {
      return false;
    }

    if (!rule.answerIndexes || !rule.answerIndexes.length || rule.answerIndexes.length !== selectedAnwerIndexes.length) {
      return false;
    }

    return rule.answerIndexes.every((ansId) => selectedAnwerIndexes.includes(ansId));
  }

  /**
   *
   * @param {HTMLElement} surveyDiv survey div
   * @param {object} result result object
   *
   */

  displayAppropriateResult(surveyDiv, result) {
    if (result.type === 'redirect' && result.content) {
      top.location.href = result.content;
    }
    if (['html', 'text'].includes(result.type) && result.content) {
      surveyDiv.innerHTML = result.content;

      const codes = surveyDiv.getElementsByTagName('script');
      for (let i = 0; i < codes.length; i += 1) {
        try {
          const script = document.createElement('script');
          if (codes[i].text) {
            const inlineScript = document.createTextNode(codes[i].text);
            script.appendChild(inlineScript);
          } else if (codes[i].src) {
            script.src = codes[i].src;
          }
          document.head.appendChild(script);
        } catch (error) {
          console.warn('[CleverPush Survey] Error appending script to survey result', error);
        }
      }
    }
    if (result.type === 'pushOptIn') {
      if (!window.CleverPush) {
        window.CleverPush = [];
      }
      window.CleverPush.push(['triggerOptIn', true]);
    }
  }

  /**
   * This function will display the results of the survey
   *
   * @function
   * @param {HTMLElement} surveyDiv Survey div
   * @param {object} config config object
   */
  displayResults({ surveyDiv, config, survey }) {
    surveyDiv.innerHTML = '';
    const questions = (survey.questions || []);
    questions.forEach((_, questionIndex) => {
      if (questions[questionIndex].hideResults || questions[questionIndex].type === NPS_SURVEY_TYPE) {
        return;
      }

      surveyDiv.innerHTML += this.getSurveyHtml({
        question: survey.questions[questionIndex],
        questionIndex,
        skipToResults: true,
        survey,
      });
      this.initClickListeners(surveyDiv, config, survey);
    });
    const textAnswers = surveyDiv.querySelectorAll('.cleverpush-survey-other-answer');
    textAnswers.forEach((element) => element.remove());
    const surveryButton = surveyDiv.querySelector('.cleverpush-survey-buttons');
    if (surveryButton) {
      surveryButton.remove();
    }
    const voteType = survey.voteType || VOTE_TYPE.PERCENTAGE;
    const completedAnswers = surveyDiv.querySelectorAll('.cleverpush-survey-answer');
    for (let j = 0; j < completedAnswers.length; j += 1) {
      let totalVoted = 0;
      completedAnswers[j].className += ' cleverpush-survey-answer-voted';
      const answerSurveyType = completedAnswers[j].dataset.surveyType;

      if (answerSurveyType === 'nps') {
        completedAnswers[j].style = 'width: 18%;';
      }

      const surveyResult = document.createElement('div');
      surveyResult.className = 'cleverpush-survey-result';

      const barText = document.createElement('div');
      barText.className = 'cleverpush-survey-answer-text';
      surveyResult.appendChild(barText);

      const bar = document.createElement('div');
      bar.className = 'cleverpush-survey-answer-bar';
      surveyResult.appendChild(bar);

      completedAnswers[j].appendChild(surveyResult);

      const otherQuestionIndex = parseInt(completedAnswers[j].dataset.question, 10);
      const otherAnswerIndex = parseInt(completedAnswers[j].dataset.answer, 10);
      survey.questions[otherQuestionIndex].answers.forEach((answer) => {
        totalVoted += parseInt(answer?.voted || 0, 10);
      });

      const voted = survey.questions[otherQuestionIndex].answers[otherAnswerIndex].voted || 0;
      let percent = totalVoted > 0 ? Math.round((((parseInt(voted, 10) || 0) / (totalVoted)) * 100)) : 0;
      if (percent > 100) {
        percent = 100;
      }

      const barInner = document.createElement('div');
      barInner.className = 'cleverpush-survey-answer-bar-inner';
      bar.appendChild(barInner);
      barText.innerText = voteType === VOTE_TYPE.ABSOLUTE_COUNT ? `${parseInt(survey.questions[otherQuestionIndex].answers[otherAnswerIndex].voted || 0, 10)}` : `${percent}%`;
      barInner.style.width = `${percent}%`;
    }

    this.completedSurveys.push(survey._id);
    const surveyAlreadyVotedKey = `cleverpush-${survey._id}-survey-already-voted`;
    try {
      localStorage.setItem(surveyAlreadyVotedKey, 'true');
    } catch (error) {
      console.log(error);
    }
    if (config.nextSurvey) {
      this.showRandom(surveyDiv, config.nextSurvey);
    }
  }

  showRandom(surveyDiv, nextSurvey) {
    const buttonArea = document.createElement('button');
    buttonArea.className = 'cleverpush-next-survey';
    buttonArea.innerHTML = translate('next.survey');
    surveyDiv.appendChild(buttonArea);

    const newSurvey = document.createElement('div');
    newSurvey.className = 'cleverpush-survey';
    newSurvey.dataset.id = nextSurvey;
    surveyDiv.parentNode.insertBefore(newSurvey, surveyDiv);

    buttonArea.addEventListener('click', () => {
      this.completedSurveys.forEach((surveyId) => {
        const classNameId = `.cleverpush-survey-${surveyId}`;
        const element = document.querySelector(classNameId);
        if (element) {
          element.remove();
        }
      });

      this.initSurveys();
    });
  }

  /**
   * This function will generate the survey html
   *
   * @param {object} question current question object
   * @param {Number} questionIndex current question index
   * @param {object} config Config object
   * @param {Boolean} skipToResults (optional) skip to results
   * @param {String} surveyId survey id
   * @returns {String} HTML string
   */
  getSurveyHtml({
    question, questionIndex, survey, skipToResults = false, surveyCompleted = false,
  }) {
    const questions = survey.questions;
    const isSingleQuestionChoice = questions[questionIndex].answerChoice === 'single';
    const hasMoreThanOneMultipleChoice = !skipToResults && questions[questionIndex].answerChoice === 'multiple';
    const isLastQuestionHasMultipleChoice = !skipToResults && (questionIndex === questions.length - 1) && (questions[questions.length - 1].answerChoice === 'multiple');
    const nextButtonText = survey.nextButtonText || translate('next.surveyQuestion');
    const submitButtonText = survey.submitButtonText || translate('survey.submit');
    const textInputPlaceHolder = question?.textInputPlaceHolder || translate('enterTextAnswer.survey');
    const surveyType = questions[questionIndex].type === NPS_SURVEY_TYPE ? NPS_SURVEY_TYPE : DEFAULT_SURVEY_TYPE;
    const surveyQuestionTitleStyle = questions[questionIndex].type === NPS_SURVEY_TYPE ? 'text-align:center;' : '';
    const surveyAnswerDisplayStyle = questions[questionIndex].type === NPS_SURVEY_TYPE ? 'display: flex; align-items: center; justify-content: center; gap: 4px; flex-wrap: wrap' : '';
    const surveyAnswerInsideStyle = questions[questionIndex].type === NPS_SURVEY_TYPE ? 'width: fit-content; margin: auto' : '';
    const surveyAnswerDivStyle = questions[questionIndex].type === NPS_SURVEY_TYPE ? 'border-radius: 10px; height: 36px; width:36px; padding: 0; display: flex; flex-direction: column; justify-content: center; align-items: center;' : '';
    const surveyAnswerParentDivStyle = questions[questionIndex].type === NPS_SURVEY_TYPE ? 'width: fit-content; margin: auto' : '';
    const surveyAnswerTitleStyle = questions[questionIndex].type === NPS_SURVEY_TYPE ? 'margin-bottom: 0px; width: auto' : '';
    const surveyNpsMinimumLabel = questions[questionIndex].npsMinimumLabel || '';
    const surveyNpsMaximumLabel = questions[questionIndex].npsMaximumLabel || '';
    const hideBranding = survey.hideBranding || false;

    if (!surveyCompleted) {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.apiEndpoint}/survey/${survey._id}/shown`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.send(JSON.stringify({
        pathname: location.pathname,
        questionIndex
      }));

      try {
        const surveyShownCount = localStorage.getItem(`cleverpush-${survey._id}-survey-shown`);
        const newCount = surveyShownCount ? parseInt(surveyShownCount, 10) + 1 : 1;
        localStorage.setItem(`cleverpush-${survey._id}-survey-shown`, newCount);
      } catch (error) {
        console.log(error);
      }
    }

    const displayFreeTextAnswerField = (isTextAnswersEnabled) => {
      if (surveyCompleted) {
        return false;
      }
      if (isTextAnswersEnabled && surveyType === DEFAULT_SURVEY_TYPE) {
        return true;
      }
      return false;
    };

    if (question.type === SPEEDOMETER_SURVEY_TYPE) {
      return getSpeedometerHtml(question, questionIndex, hideBranding);
    }

    question.answers = question.answers.filter((answer) => answer.title);

    return `
      <div class="cleverpush-survey-questions">
        <div class="cleverpush-survey-question">
        <div class="cleverpush-survey-answers" style="${surveyAnswerInsideStyle}">
        <div class="cleverpush-survey-question-title" style="${surveyQuestionTitleStyle}">${question.title || ''}</div>
        <div class="cleverpush-survey-question-main-answer" style="${surveyAnswerParentDivStyle}">
              <div class="cleverpush-survey-answers-display" style="${surveyAnswerDisplayStyle}">
                ${(question.answers || []).filter((answer) => answer?.title).map((answer, answerIndex) => (`
                  <div class="cleverpush-survey-answer" style="${surveyAnswerDivStyle}" data-question="${questionIndex}" data-answer="${answerIndex}" data-id="${survey._id}" data-survey-type="${surveyType}">
                    <div class="cleverpush-survey-answer-title" style="${surveyAnswerTitleStyle}">
                      ${(!skipToResults && question.answerChoice === 'multiple') ? `
                        <input type="checkbox" id="checkbox-${answerIndex}" data-id="${survey._id}" data-question="${questionIndex}" data-answer="${answerIndex}" class="cleverpush-survey-answer-checkbox" />
                      ` : ''}
                      ${answer.title || ''}
                    </div>
                  </div>
                `)).join('')}

                ${question?.textAnswersEnabled && question?.answers?.length > 1 && question?.type === DEFAULT_SURVEY_TYPE ? `
                  <div class="cleverpush-survey-answer cleverpush-survey-other-answer" style="${surveyAnswerDivStyle}" data-question="${questionIndex}" data-answer="${question?.answers?.length}" data-id="${survey._id}" data-survey-type="${surveyType}">
                    <div class="cleverpush-survey-answer-title" style="${surveyAnswerTitleStyle}">
                      ${(!skipToResults && question.answerChoice === 'multiple') ? `
                        <input type="checkbox" id="checkbox-${question?.answers?.length}" data-id="${survey._id}" data-question="${questionIndex}" data-answer="${question?.answers?.length}" class="cleverpush-survey-answer-checkbox cleverpush-survey-other-answer-checkbox" />
                      ` : ''}
                      ${question?.otherAnswerLabel || 'Other'}
                    </div>
                  </div>
                ` : ''}
              </div>

              ${surveyType === NPS_SURVEY_TYPE && !skipToResults ? `
                <div class="cleverpush-survey-answers-label">
                  <div class="cleverpush-survey-answer-label">${surveyNpsMinimumLabel}</div>
                  <div class="cleverpush-survey-answer-label">${surveyNpsMaximumLabel}</div>
                </div>
              ` : ''}
              </div>
            </div>

            ${(displayFreeTextAnswerField(question?.textAnswersEnabled)) ? `
            <div style="${question?.answers?.length >= 1 ? 'display: none;' : ''}" class="cleverpush-survey-text-answer cleverpush-survey-buttons">
              <div class="cleverpush-survey-input-container">
                <textarea rows="4" class="cleverpush-survey-text-inputanswer" id="cleverpush-survey-text-${questionIndex}" data-question="${questionIndex}" data-id="${survey._id}" type="text" placeholder="${textInputPlaceHolder}" maxlength="${TEXT_MAX_LENGTH}" ${!question?.textAnswersOptional ? ' required' : ''}></textarea>
                <button style="${question?.textAnswerButtonBelow ? 'display: none;' : ''}" class="cleverpush-survey-button cleverpush-survey-button-submit cleverpush-survey-button-single" data-question="${questionIndex}" data-id="${survey._id}">
                  ${submitButtonText}
                </button>
              </div>
            </div>
            ` : ''}

            <div class="cleverpush-survey-buttons cleverpush-survey-buttons-bottom" style="${(question?.answers?.length > 1 && question?.textAnswerButtonBelow && question?.answerChoice === 'single') ? 'display: none;' : ''}">
              ${questionIndex < questions?.length - 1 && ((displayFreeTextAnswerField(question?.textAnswersEnabled) && question?.textAnswerButtonBelow) || ((hasMoreThanOneMultipleChoice && !isLastQuestionHasMultipleChoice) || question?.answers?.length === 0)) ? `
                <button class="cleverpush-survey-button cleverpush-survey-button-next" data-id="${survey._id}" data-click-listener="${!skipToResults ? 'true' : 'false'}">
                  ${nextButtonText}
                </button>
              ` : ''}
              ${(questionIndex === questions?.length - 1 && ((displayFreeTextAnswerField(question?.textAnswersEnabled) && question?.textAnswerButtonBelow) || (isLastQuestionHasMultipleChoice && question.answerChoice === 'multiple'))) ? `
                <button class="cleverpush-survey-button cleverpush-survey-button-submit" data-question="${questionIndex}" data-id="${survey._id}">
                  ${submitButtonText}
                </button>
              ` : ''}
            </div>
        </div>
      </div>
    `;
  }

  /**
   * This function will submit the votes to db
   *
   * @param {String} surveyId SurveyId
   * @param {Number} questionIndex Index of Question
   * @param {Number} answerIndex Index of Answer
   */

  setAttributes(config) {
    const attributes = config.survey.attributes || [];
    this.attributes = {};

    attributes.forEach((attribute) => {
      let evaluatedValue;
      try {
        evaluatedValue = eval(attribute.javascriptFunction);
      } catch (error) {
        console.warn(`[CleverPush Survey] Error evaluating attribute ${attribute.id}: ${error}`);
      }
      this.attributes[attribute.id] = evaluatedValue;
    });
    return this.attributes;
  }

  sendVotes({
    surveyId,
    questionIndex,
    answerIndex,
    previousOpinion,
    opinion,
    isLastQuestion,
    isVoted
  }) {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${this.apiEndpoint}/survey/${surveyId}/vote`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');

    xhr.send(JSON.stringify({
      questionIndex,
      answerIndex,
      attributes: this.attributes,
      answers: this.currentSurveyAnswers,
      previousOpinion,
      opinion,
      isLastQuestion,
      isVoted,
      pathname: location.pathname,
    }));

    const surveyQuestionAnswered = {
      surveyId,
      questionIndex
    };
    try {
      localStorage.setItem(`cleverpush-${surveyId}-survey-question-answered`, JSON.stringify(surveyQuestionAnswered));
      if (opinion) {
        localStorage.setItem(`cleverpush-${surveyId}-survey-question-${questionIndex}-previous-opinion`, opinion);
      }
      localStorage.setItem(`cleverpush-${surveyId}-survey-question-${questionIndex}-answered`, JSON.stringify(surveyQuestionAnswered));
    } catch (error) {
      console.log(error);
    }
  }

  initSpeedometer(config) {
    speedometer(config, this.sendVotes.bind(this));
  }
}

new CleverPushSurvey();
